import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { FC, ReactNode, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import NavigationItemContent from "./navigation-item-content";
import NavigationItemSubtree from "./navigation-item-subtree";

export type NavigationItemProps = {
  label: string;
  href: string;
  icon: ReactNode;
  disabled?: boolean;
  disabledReason?: string;
  navChildren?: {
    label: string;
    href: string;
    disabled?: boolean;
    disabledReason?: string;
  }[];
};

const className =
  "flex h-11 items-center gap-4 rounded-md px-3 text-sm font-bold leading-4 text-[#4F5D6D]";

const classNameActive = "bg-[#262452] text-white hover:text-white";

const NavigationItem: FC<NavigationItemProps> = (props) => {
  const hasNavChildren = (props.navChildren?.length || 0) > 1;

  const navLinkRef = useRef<HTMLAnchorElement>(null);

  // Observe changes in the parent element of the NavLink to update the
  // active class when aria-current="page" (set by react-router-dom) is
  // set on a child link. This is needed because the active class is
  // applied based on the current route, and parent elements may have
  // the link of the first child in the tree
  useEffect(() => {
    if (!navLinkRef.current) {
      return;
    }

    const parentElement = navLinkRef.current.parentElement;

    const observer = new MutationObserver(() => {
      const activeLinks = parentElement?.querySelectorAll(
        'a[aria-current="page"]',
      );

      const hasActiveLinks = activeLinks && activeLinks.length > 0;

      if (hasActiveLinks) {
        navLinkRef?.current?.classList.add(...classNameActive.split(" "));
      } else {
        navLinkRef?.current?.classList.remove(...classNameActive.split(" "));
      }
    });

    if (parentElement) {
      observer.observe(parentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["aria-current"],
      });
    }

    return () => observer.disconnect();
  }, [navLinkRef.current]);

  return props.disabled ? (
    <>
      <Tooltip>
        <TooltipTrigger>
          <NavigationItemContent
            className={cn(className, "cursor-not-allowed opacity-60")}
            icon={props.icon}
            label={props.label}
            showArrow={hasNavChildren}
          />
        </TooltipTrigger>
        {props.disabledReason && (
          <TooltipContent side="right">{props.disabledReason}</TooltipContent>
        )}
      </Tooltip>

      <NavigationItemSubtree subtree={props.navChildren} />
    </>
  ) : (
    <>
      <NavLink
        ref={navLinkRef}
        className={({ isActive }) =>
          cn(className, "hover:text-[#121d28]", isActive && classNameActive)
        }
        to={props.href}
      >
        <NavigationItemContent
          className="contents"
          icon={props.icon}
          label={props.label}
          showArrow={hasNavChildren}
        />
      </NavLink>

      <NavigationItemSubtree subtree={props.navChildren} />
    </>
  );
};

export default NavigationItem;
